/**
 * Backup nagging, and the month-end block.
 *
 * The reasoning, because it is the least popular feature in the app: trip
 * records must be kept for ten years (§ 147 Abs. 1 Nr. 1 AO), GoBD Rz. 103-104
 * makes securing them against loss the taxpayer's own duty, and records that
 * cannot be produced make the Buchführung formally not ordnungsmäßig — which
 * opens the door to an estimate under § 162 AO.
 *
 * On top of that, iOS can evict IndexedDB under storage pressure even for an
 * installed PWA. So for a local-only app, an unenforced backup is not a
 * feature, it is a countdown.
 */

import { daysSince } from '@/domain/dates';
import type { AppSettings } from '@/domain/types';
import { de } from '@/i18n/de';
import type { Dictionary } from '@/i18n/de';

export type BackupSeverity = 'ok' | 'due' | 'overdue' | 'never';

export interface BackupStatus {
  severity: BackupSeverity;
  daysSinceBackup: number | null;
  /** Show a persistent banner. */
  warn: boolean;
  /** Refuse to close a month until a backup is taken. */
  block: boolean;
  /**
   * Rendered late, with the caller's dictionary: the severity is a fact about
   * the data, the wording belongs to whoever is looking at the screen.
   */
  message: (t?: Dictionary) => string;
}

export function assessBackup(settings: AppSettings, now = Date.now()): BackupStatus {
  const days = settings.lastBackupAt
    ? Math.floor((now - new Date(settings.lastBackupAt).getTime()) / 86_400_000)
    : null;

  if (days === null) {
    return {
      severity: 'never',
      daysSinceBackup: null,
      warn: true,
      block: false,
      message: (t = de) => t.backupStatus.never,
    };
  }

  if (days >= settings.backupBlockAfterDays) {
    return {
      severity: 'overdue',
      daysSinceBackup: days,
      warn: true,
      block: true,
      message: (t = de) => t.backupStatus.overdue(days),
    };
  }

  if (days >= settings.backupWarnAfterDays) {
    return {
      severity: 'due',
      daysSinceBackup: days,
      warn: true,
      block: false,
      message: (t = de) => t.backupStatus.due(days),
    };
  }

  return {
    severity: 'ok',
    daysSinceBackup: days,
    warn: false,
    block: false,
    message: (t = de) => (days === 0 ? t.backupStatus.today : t.backupStatus.ok(days)),
  };
}

export function formatLastBackup(settings: AppSettings, t: Dictionary = de): string {
  const days = daysSince(settings.lastBackupAt);
  if (days === null) return t.backupStatus.lastNever;
  if (days === 0) return t.backupStatus.lastToday;
  if (days === 1) return t.backupStatus.lastYesterday;
  return t.backupStatus.lastDaysAgo(days);
}

/**
 * Storage headroom, so the app can warn before the browser starts evicting.
 * Returns null where the Storage API is unavailable, which includes older
 * iOS Safari.
 */
export async function storageEstimate(): Promise<{
  usedBytes: number;
  quotaBytes: number;
  percentUsed: number;
} | null> {
  if (!navigator.storage?.estimate) return null;
  const estimate = await navigator.storage.estimate();
  const usedBytes = estimate.usage ?? 0;
  const quotaBytes = estimate.quota ?? 0;
  if (quotaBytes === 0) return null;
  return { usedBytes, quotaBytes, percentUsed: (usedBytes / quotaBytes) * 100 };
}

/**
 * Ask the browser to exempt our storage from eviction.
 *
 * Safari grants this automatically for a home-screen web app and ignores the
 * call otherwise; Chrome grants it based on engagement heuristics. Worth
 * asking, never worth relying on — which is why the backup nag exists.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  if (await navigator.storage.persisted?.()) return true;
  return navigator.storage.persist();
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toLocaleString('de-DE', { maximumFractionDigits: 1 })} ${units[unitIndex]}`;
}
